import React, { forwardRef, useState, useEffect } from 'react';
import { Button, Switch, Box, Dialog, Container, AppBar, Toolbar, IconButton, Typography, Slide, Paper, Alert, AlertTitle, FormControl, InputLabel, Select, MenuItem, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import Grid from '@mui/material/Grid';
import CloseIcon from '@mui/icons-material/Close';
import ModeStandbyIcon from '@mui/icons-material/ModeStandby';
import LinkIcon from '@mui/icons-material/Link';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import DevicesIcon from '@mui/icons-material/Devices';
import PieChartIcon from '@mui/icons-material/PieChart';
import ShareIcon from '@mui/icons-material/Share';
import { format } from 'date-fns';
import { printStyleSection, printStyleCover, printStyleBreakAfter, printStyleOverrides } from '../../theme/constants';
import Hotspot from '../views/Hotspot';
import Summary from '../views/Summary';
import Social from '../views/Social';
import Device from '../views/Device';
import CampaignHeatmap from '../CampaignHeatmap';
import PrintReportTable from '../PrintReportTable';
import Links from '../views/Links';
import LoadingOverlay from '../../loadingOverlay/LoadingOverlay';
import usePrevious from '../../hooks/usePrevious';
import useSuspenseFetch from '../../hooks/useSuspenseFetch';

const numberOfContactsAvailable = [10, 25, 50];

const Transition = forwardRef((props, ref) => {
    return <Slide direction="up" ref={ref} {...props} />;
});

Transition.displayName = 'Transition';

const Print = ({ email, html, links }) => {
    const { data: account } = useSuspenseFetch('account');
    const { company, logo } = account;
    const [openDialog, setOpenDialog] = useState(false);
    const [showLoadingOverlay, setShowLoadingOverlay] = useState(true);
    const [showHotspot, setShowHotspot] = useState(true);
    const [showLinks, setShowLinks] = useState(true);
    const [showHeatmap, setShowHeatmap] = useState(true);
    const [showSocial, setShowSocial] = useState(true);
    const [showData, setShowData] = useState(true);
    const [showDevice, setShowDevice] = useState(true);
    const [loadingSummary, setLoadingSummary] = useState(true);
    const [loadingDataOpens, setLoadingDataOpens] = useState(true);
    const [loadingDataClicks, setLoadingDataClicks] = useState(true);
    const [loadingHeatmap, setLoadingHeatmap] = useState(true);
    const [loadingSocial, setLoadingSocial] = useState(true);
    const [loadingDevice, setLoadingDevice] = useState(true);
    const [numberOfContacts, setNumberOfContacts] = useState(numberOfContactsAvailable[0]);
    const previousOpenDialog = usePrevious(openDialog);

    useEffect(() => {
        //only wait for items to load if that item is chosen to be shown
        const dataClicksComplete = showData ? !loadingDataClicks ? true : false : true;
        const dataOpensComplete = showData ? !loadingDataOpens ? true : false : true;
        const heatmapComplete = showHeatmap ? !loadingHeatmap ? true : false : true;
        const socialComplete = showSocial ? !loadingSocial ? true : false : true;
        const deviceComplete = showDevice ? !loadingDevice ? true : false : true;

        //If all optional and non-optional items are complete, we're ready
        if (!loadingSummary && dataOpensComplete && dataClicksComplete && heatmapComplete && socialComplete && deviceComplete) {
            setShowLoadingOverlay(false);
        }
    }, [loadingSummary, showData, showHeatmap, showSocial, showDevice, loadingDataClicks, loadingDataOpens, loadingHeatmap, loadingSocial, loadingDevice]);

    useEffect(() => {
        if (openDialog !== previousOpenDialog) {
            //handle user closing dialog and repicking options
            if (!openDialog) {
                setLoadingDataOpens(true);
                setLoadingDataClicks(true);
                setLoadingHeatmap(true);
                setLoadingSocial(true);
                setLoadingDevice(true);
                setLoadingSummary(true);
                setShowLoadingOverlay(true);
            }
        }
    }, [openDialog]);

    return (
        <>
            <Box sx={{ padding: 3 }}>
                <Paper sx={{ marginTop: 3, px: 3 }}>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, lg: 4 }}>
                            <List>
                                <ListItem>
                                    <ListItemIcon>
                                        <ModeStandbyIcon />
                                    </ListItemIcon>
                                    <ListItemText id="hotspots" primary="Email view with hotspots" />
                                    <Switch
                                        edge="end"
                                        onChange={e => setShowHotspot(e.target.checked)}
                                        checked={showHotspot}
                                        slotProps={{
                                            input: {
                                                'aria-labelledby': 'hotspots'
                                            }
                                        }}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <LinkIcon />
                                    </ListItemIcon>
                                    <ListItemText id="links" primary="Link summary table" />
                                    <Switch
                                        edge="end"
                                        onChange={e => setShowLinks(e.target.checked)}
                                        checked={showLinks}
                                        slotProps={{
                                            input: {
                                                'aria-labelledby': 'links'
                                            }
                                        }}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <ThermostatIcon />
                                    </ListItemIcon>
                                    <ListItemText id="heatmap" primary="Peak engagement heatmap" />
                                    <Switch
                                        edge="end"
                                        onChange={e => setShowHeatmap(e.target.checked)}
                                        checked={showHeatmap}
                                        slotProps={{
                                            input: {
                                                'aria-labelledby': 'heatmap'
                                            }
                                        }}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <ShareIcon />
                                    </ListItemIcon>
                                    <ListItemText id="social" primary="Social summary" />
                                    <Switch
                                        edge="end"
                                        onChange={e => setShowSocial(e.target.checked)}
                                        checked={showSocial}
                                        slotProps={{
                                            input: {
                                                'aria-labelledby': 'social'
                                            }
                                        }}
                                    />
                                </ListItem>
                                <ListItem sx={{ border: '1px solid #eee' }}>
                                    <ListItemIcon>
                                        <PieChartIcon />
                                    </ListItemIcon>
                                    <ListItemText
                                        id="data"
                                        primary="Data"
                                        secondary={(
                                            <React.Fragment>
                                                <FormControl size="small" sx={{ display: 'block' }}>
                                                    <InputLabel id="data-count-label">Number of Contacts</InputLabel>
                                                    <Select
                                                        labelId="data-count-label"
                                                        id="data-count"
                                                        value={numberOfContacts}
                                                        label="Number of Contacts"
                                                        onChange={event => setNumberOfContacts(event.target.value)}
                                                        sx={{ display: 'block' }}
                                                    >
                                                        {numberOfContactsAvailable.map((e, i) => <MenuItem key={i} value={e}>{e}</MenuItem>)}
                                                    </Select>
                                                </FormControl>
                                            </React.Fragment>
                                        )}
                                    />
                                    <Switch
                                        edge="end"
                                        onChange={e => setShowData(e.target.checked)}
                                        checked={showData}
                                        slotProps={{
                                            input: {
                                                'aria-labelledby': 'data'
                                            }
                                        }}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <DevicesIcon />
                                    </ListItemIcon>
                                    <ListItemText id="device" primary="Device and Mail Apps" />
                                    <Switch
                                        edge="end"
                                        onChange={e => setShowDevice(e.target.checked)}
                                        checked={showDevice}
                                        slotProps={{
                                            input: {
                                                'aria-labelledby': 'device'
                                            }
                                        }}
                                    />
                                </ListItem>
                            </List>
                        </Grid>
                        <Grid size={{ xs: 12, lg: 8 }} sx={{ alignItems: 'center' }}>
                            <Alert severity="info">
                                <AlertTitle>Printing guidance</AlertTitle>
                                <Typography variant="body2" sx={{ marginBottom: 0.5 }}>
                                    For the best visual experience, turn on 'Background graphics' in your browser's print dialog.
                                </Typography>
                                <Typography variant="body2">
                                    In Chrome and Edge, this option can be found in 'More settings'. In Firefox and Safari this option is called 'Print backgrounds'.
                                </Typography>
                            </Alert>
                            <Button
                                onClick={() => setOpenDialog(true)}
                                color="secondary"
                                sx={{ marginTop: 2 }}
                            >
                                Generate Manager's Report
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
            </Box>
            <Dialog
                fullScreen
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                slots={{ transition: Transition }}
                sx={printStyleOverrides}
            >
                <AppBar sx={{ position: 'relative', displayPrint: 'none' }}>
                    <Toolbar>
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={() => setOpenDialog(false)}
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                        <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                            Manager's Report: {email.subject}
                        </Typography>
                        <Button onClick={() => window.print()}>
                            Print
                        </Button>
                    </Toolbar>
                </AppBar>
                <Paper elevation={0} square>
                    <Container maxWidth="lg">
                        <Box sx={{ ...printStyleCover, ...printStyleBreakAfter }}>
                            <Box sx={{ paddingBottom: 4 }}>
                                {logo ? (
                                    <img src={logo} style={{ maxWidth: 400, height: 'auto' }} />
                                ) : (
                                    <Typography
                                        variant="h3"
                                    >
                                        {company}
                                    </Typography>
                                )}
                            </Box>
                            <Typography
                                variant="h2"
                            >
                                Email Marketing Report
                            </Typography>
                            <Typography
                                variant="h4"
                                sx={{ paddingBottom: 0, paddingTop: 2 }}
                            >
                                {email.name}
                            </Typography>
                            <Typography
                                variant="h4"
                                sx={{ paddingBottom: 2, paddingTop: 0 }}
                            >
                                Subject: {email.subject}
                            </Typography>
                            <Typography
                                variant="body1"
                            >
                                Sent {format((new Date(email.sendDateTime)), 'dd/MM/yyyy HH:mm')}
                            </Typography>
                            <Typography
                                variant="body1"
                            >
                                Report created: {format((new Date()), 'dd/MM/yyyy HH:mm')}
                            </Typography>
                        </Box>
                        <Box sx={printStyleSection}>
                            <Typography
                                variant="h5"
                            >
                                Summary
                            </Typography>
                            <Summary
                                campaign={email}
                                isTabSelected={true}
                                isReport={true}
                                parentIsLoading={loadingSummary}
                                setParentIsLoading={loadingSummary => setLoadingSummary(loadingSummary)}
                            />
                        </Box>
                        {showHotspot && (
                            <Box sx={printStyleSection}>
                                <Typography
                                    variant="h5"
                                >
                                    Hotspot
                                </Typography>
                                <Hotspot
                                    html={html}
                                    itemLinks={links}
                                    onViewWhoClicked={null}
                                />
                            </Box>
                        )}
                        {showLinks && (
                            <Box sx={printStyleSection}>
                                <Typography
                                    variant="h5"
                                >
                                    Link summary table
                                </Typography>
                                <Links
                                    campaignId={email.id}
                                    onViewWhoClicked={null}
                                    linkId={null}
                                    allLinks={links}
                                    parentIsLoading={showLoadingOverlay}
                                />
                            </Box>
                        )}
                        {showData && (
                            <Box sx={printStyleSection}>
                                <Typography
                                    variant="h5"
                                >
                                    Data
                                </Typography>
                                <Typography
                                    variant="h6"
                                >
                                    Top {numberOfContacts} contacts ordered by Opens
                                </Typography>
                                <PrintReportTable
                                    campaignId={email.id}
                                    subscriberStatus="opened"
                                    parentIsLoading={loadingDataOpens}
                                    setParentIsLoading={loadingDataOpens => setLoadingDataOpens(loadingDataOpens)}
                                    numberOfContacts={numberOfContacts}
                                />
                                <Typography
                                    variant="h6"
                                >
                                    Top {numberOfContacts} contacts ordered by Clicks
                                </Typography>
                                <PrintReportTable
                                    campaignId={email.id}
                                    subscriberStatus="clicked"
                                    parentIsLoading={loadingDataClicks}
                                    setParentIsLoading={loadingDataClicks => setLoadingDataClicks(loadingDataClicks)}
                                    numberOfContacts={numberOfContacts}
                                />
                            </Box>
                        )}
                        {showHeatmap && (
                            <Box sx={printStyleSection}>
                                <Typography
                                    variant="h5"
                                >
                                    Peak engagement heatmap
                                </Typography>
                                <CampaignHeatmap
                                    campaignId={email.id}
                                    parentIsLoading={loadingHeatmap}
                                    setParentIsLoading={loadingHeatmap => setLoadingHeatmap(loadingHeatmap)}
                                />
                            </Box>
                        )}
                        {showDevice && (
                            <Box sx={printStyleSection}>
                                <Typography
                                    variant="h5"
                                >
                                    Device and Mail Apps
                                </Typography>
                                <Device
                                    campaignId={email.id}
                                    parentIsLoading={loadingDevice}
                                    setParentIsLoading={loadingDevice => setLoadingDevice(loadingDevice)}
                                    isReport={true}
                                />
                            </Box>
                        )}
                        {showSocial && (
                            <Box sx={printStyleSection}>
                                <Typography
                                    variant="h5"
                                >
                                    Social summary
                                </Typography>
                                <Social
                                    campaignId={email.id}
                                    campaignUniqueOpens={email.uniqueOpens}
                                    isReport={true}
                                    parentIsLoading={loadingSocial}
                                    setParentIsLoading={loadingSocial => setLoadingSocial(loadingSocial)}
                                />
                            </Box>
                        )}
                    </Container>
                </Paper>
                {showLoadingOverlay && <LoadingOverlay />}
            </Dialog>
        </>
    );
};

export default Print;